import { useState } from 'react';
import type { Trade, UseGameStateReturn } from '../../hooks/useGameState';
import { AuthUser } from '../../services/authService';
import {
  BROKERAGE_FEE_RATE,
  TRANSACTION_TAX_RATE,
  calculateBrokerageFee,
  calculateBuyCost,
  calculateStockValue,
  calculateTransactionTax,
} from '../../shared/utils/tradingFees';
import StockChart from '../StockChart';
import { loadRefreshToken } from '../../utils/storage';
import { decodeJwt } from '../../utils/jwt';

interface GameMainProps {
  game: UseGameStateReturn;
  user: AuthUser | null;
  showRankingModal: boolean;
  onLoginClick: () => void;
}

export const GameMain = ({
  game,
  user,
  showRankingModal,
  onLoginClick,
}: GameMainProps) => {
  const { state, setters, actions, helpers } = game;
  const {
    gameState,
    theme,
    quantity,
    tradeHistory,
    isAutoMode,
    showGameEndPopup,
    showExitConfirmPopup,
    gameEndData,
    isMarketOrder,
    limitOrderPrice,
    activeTradeTab,
    showOHLCInfo,
    showBalanceResetPopup,
    toast,
    stockInfo,
    changeRate,
    ma5,
    ma10,
    ma20,
    ma60,
    ma120,
  } = state;
  const {
    setQuantity,
    setIsAutoMode,
    setShowGameEndPopup,
    setShowExitConfirmPopup,
    setIsMarketOrder,
    setLimitOrderPrice,
    setActiveTradeTab,
    setShowOHLCInfo,
    setShowBalanceResetPopup,
  } = setters;
  const {
    startGame,
    generateNextCandle,
    buy,
    sell,
    cancelLimitOrder,
    calculateBuyPercentage,
    calculateSellPercentage,
    endGame,
    resetBalance,
    dismissToast,
  } = actions;
  const { getTickSize, adjustToTickSize, formatPrice, formatBalance } = helpers;

  const [showFeeInfoModal, setShowFeeInfoModal] = useState(false);
  const [showSessionWarningModal, setShowSessionWarningModal] = useState(false);
  const [sessionRemainingSeconds, setSessionRemainingSeconds] = useState(0);

  const normalizeTradeTypeLabel = (rawType: string) => {
    const t = (rawType || '').toLowerCase();
    if (t === 'buy' || rawType === '매수') return '매수';
    if (t === 'sell' || rawType === '매도') return '매도';
    return rawType;
  };

  const isStockTradeType = (rawType: string) => {
    const t = (rawType || '').toLowerCase();
    return t === 'buy' || t === 'sell' || rawType === '매수' || rawType === '매도';
  };

  const renderTradeHistory = (history: Trade[]) =>
    history.map((item, idx) => (
      <li
        key={idx}
        className={`trade-history-item ${
          item.type === '롱'
            ? 'long'
            : item.type === '숏'
            ? 'short'
            : item.type === '매수'
            ? 'buy'
            : item.type === '매도'
            ? 'sell'
            : 'close'
        }`}
      >
        <div className="trade-info">
          <span className="trade-type">{item.type}</span>
          <span className="trade-price">{formatPrice(item.price)}</span>
        </div>
        <div className="trade-details">
          <span className="trade-size">
            {item.size.toLocaleString()}
            {item.type === '매수' || item.type === '매도' ? '주' : '개'}
          </span>
          <span className="trade-time">{item.time.toLocaleTimeString()}</span>
          {item.profit !== undefined && item.profit !== 0 && (
            <span
              className={`trade-profit ${
                item.profit >= 0 ? 'positive' : 'negative'
              }`}
            >
              {item.profit > 0 ? '+' : ''}
              {item.profit.toFixed(2)}%
            </span>
          )}
        </div>
      </li>
    ));

  return (
    <>
      {toast && (
        <div className="toast-container" onClick={dismissToast}>
          <div className={`toast toast-${toast.type}`}>
            <span className="toast-message">{toast.message}</span>
          </div>
        </div>
      )}
      {showGameEndPopup && gameEndData && (
        <div className="game-end-popup-overlay">
          <div className="game-end-popup">
            <h2>게임이 종료되었습니다!</h2>
            {gameEndData.finalStockQuantity > 0 && (
              <p className="forced-liquidation-note" style={{ marginBottom: '32px' }}>
                <strong>※ 자동 청산 안내</strong>
                <br />
                보유 수량이 마지막 봉 종가 기준으로 <strong>전량 청산(매도)</strong>
                되었습니다.
              </p>
            )}

            {stockInfo && (
              <div className="stock-info">
                <h3>게임 정보</h3>
                <div className="stock-details">
                  <div className="stock-item">
                    <span className="stock-label">종목:</span>
                    <span className="stock-value">
                      {stockInfo.stockName || stockInfo.stockCode || 'N/A'}
                      {stockInfo.stockName && stockInfo.stockCode && ` (${stockInfo.stockCode})`}
                    </span>
                  </div>
                  <div className="stock-item">
                    <span className="stock-label">거래 기간:</span>
                    <span className="stock-value">
                      {new Date(stockInfo.startDate).toLocaleDateString(
                        'ko-KR'
                      )}{' '}
                      ~{' '}
                      {new Date(stockInfo.endDate).toLocaleDateString('ko-KR')}
                    </span>
                  </div>
                </div>
              </div>
            )}

            <div className="game-result">
              <div className="result-item">
                <span className="result-label">총 수익률:</span>
                <span
                  className={`result-value ${(() => {
                    // 주식 평가액은 매도 수수료와 거래세를 차감한 금액
                    const stockValue = calculateStockValue(
                      gameEndData.finalCurrentPrice,
                      gameEndData.finalStockQuantity
                    );
                    const totalAssets = gameEndData.finalBalance + stockValue;
                    // 총자산이 0원이면 negative 반환
                    if (totalAssets <= 0) {
                      return 'negative';
                    }
                    const startingBalance = Math.max(
                      gameEndData.startingBalance,
                      1
                    );
                    const totalProfitRate =
                      ((totalAssets - startingBalance) / startingBalance) * 100;
                    if (totalProfitRate > 0) return 'positive';
                    if (totalProfitRate < 0) return 'negative';
                    return 'neutral';
                  })()}`}
                >
                  {(() => {
                    // 주식 평가액은 매도 수수료와 거래세를 차감한 금액
                    const stockValue = calculateStockValue(
                      gameEndData.finalCurrentPrice,
                      gameEndData.finalStockQuantity
                    );
                    const totalAssets = gameEndData.finalBalance + stockValue;
                    const startingBalance = Math.max(
                      gameEndData.startingBalance,
                      1
                    );
                    // 총자산이 0원이면 -100% 반환
                    if (totalAssets <= 0) {
                      return '-100.00%';
                    }
                    const totalProfitRate =
                      ((totalAssets - startingBalance) / startingBalance) * 100;
                    return `${
                      totalProfitRate > 0 ? '+' : ''
                    }${totalProfitRate.toFixed(2)}%`;
                  })()}
                </span>
              </div>
              <div className="result-item">
                <span className="result-label">총 거래 횟수:</span>
                <span className="result-value">
                  {gameEndData.finalTradeHistory.length || 0}회
                </span>
              </div>
              <div className="result-item">
                <span className="result-label">생성된 봉:</span>
                <span className="result-value">
                  {gameEndData.finalCandleCount || 0}개
                </span>
              </div>
              <div className="result-item">
                <span className="result-label">최종 자산:</span>
                <span className="result-value">
                  {formatBalance(
                    gameEndData.finalBalance +
                      gameEndData.finalStockQuantity *
                        gameEndData.finalCurrentPrice
                  )}
                </span>
              </div>
            </div>
            {gameEndData.unlockedAchievements &&
              (gameEndData.unlockedAchievements.unlockedTitles.length > 0 ||
                gameEndData.unlockedAchievements.unlockedAchievements?.length >
                  0) && (
                <div className="unlocked-achievements">
                  <h3>🎉 새로 획득한 칭호/업적</h3>
                  <div className="achievements-list">
                    {gameEndData.unlockedAchievements.unlockedTitles.length >
                      0 && (
                      <div className="achievements-group">
                        <h4>칭호</h4>
                        <div className="achievements-items">
                          {gameEndData.unlockedAchievements.unlockedTitles.map(
                            (title, idx) => (
                              <div key={idx} className="achievement-item">
                                <span className="achievement-icon">
                                  {title.icon}
                                </span>
                                <span className="achievement-name">
                                  {title.name}
                                </span>
                              </div>
                            )
                          )}
                        </div>
                      </div>
                    )}
                    {gameEndData.unlockedAchievements.unlockedAchievements
                      ?.length > 0 && (
                      <div className="achievements-group">
                        <h4>업적</h4>
                        <div className="achievements-items">
                          {gameEndData.unlockedAchievements.unlockedAchievements.map(
                            (achievement, idx) => (
                              <div key={idx} className="achievement-item">
                                <span className="achievement-icon">
                                  {achievement.icon}
                                </span>
                                <span className="achievement-name">
                                  {achievement.name}
                                </span>
                              </div>
                            )
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            <div className="trade-summary">
              <h3>거래 내역 요약</h3>
              <div className="trade-summary-list">
                {gameEndData.finalTradeHistory
                  .slice(0, 10)
                  .map((trade, idx) => (
                    <div key={idx} className="trade-summary-item">
                      <span className="trade-type">
                        {normalizeTradeTypeLabel(trade.type)}
                      </span>
                      <span className="trade-price">
                        {formatPrice(trade.price)}
                      </span>
                      <span className="trade-size">
                        {trade.size.toLocaleString()}
                        {isStockTradeType(trade.type) ? '주' : '개'}
                      </span>
                    </div>
                  ))}
                {gameEndData.finalTradeHistory.length > 10 && (
                  <div className="trade-summary-more">
                    ... 외 {gameEndData.finalTradeHistory.length - 10}건
                  </div>
                )}
              </div>
            </div>
            <div
              style={{
                display: 'flex',
                gap: '0.5rem',
                justifyContent: 'center',
              }}
            >
              <button
                onClick={() => {
                  setShowGameEndPopup(false);
                }}
                className="close-popup-btn"
              >
                확인
              </button>
            </div>
          </div>
        </div>
      )}

      {showExitConfirmPopup && (
        <div className="game-end-popup-overlay">
          <div className="exit-confirm-popup">
            <h2>게임을 종료하시겠습니까?</h2>
            <p>현재 진행 중인 게임이 종료되고 결과가 표시됩니다.</p>
            <div className="exit-confirm-buttons">
              <button
                onClick={() => {
                  endGame();
                  setShowExitConfirmPopup(false);
                  setShowGameEndPopup(true);
                }}
                className="confirm-btn yes-btn"
              >
                예
              </button>
              <button
                onClick={() => setShowExitConfirmPopup(false)}
                className="confirm-btn no-btn"
              >
                아니오
              </button>
            </div>
          </div>
        </div>
      )}

      {showBalanceResetPopup && (
        <div className="game-end-popup-overlay">
          <div className="exit-confirm-popup">
            <h2>잔고가 부족합니다</h2>
            <p>
              현재 잔고가 0원입니다. 게임을 시작하려면 초기 자본 1,000만원으로
              리셋해야 합니다.
            </p>
            <div className="exit-confirm-buttons">
              <button
                onClick={() => {
                  resetBalance();
                }}
                className="confirm-btn yes-btn"
              >
                잔고 리셋하기
              </button>
              <button
                onClick={() => setShowBalanceResetPopup(false)}
                className="confirm-btn no-btn"
              >
                취소
              </button>
            </div>
          </div>
        </div>
      )}

      {showSessionWarningModal && (
        <div className="game-end-popup-overlay">
          <div className="exit-confirm-popup">
            <h2>⚠️ 세션 만료 경고</h2>
            <p style={{ marginBottom: '1rem' }}>
              <strong>로그인 세션이 곧 만료됩니다!</strong>
            </p>
            <p style={{ fontSize: '0.95rem', color: '#666', marginBottom: '1.5rem' }}>
              {sessionRemainingSeconds > 0 
                ? `남은 시간: ${sessionRemainingSeconds}초`
                : '세션이 이미 만료되었습니다'}
            </p>
            <p style={{ fontSize: '0.9rem', marginBottom: '1.5rem' }}>
              게임 중에 세션이 만료되면 <strong style={{ color: '#e74c3c' }}>진행 중인 데이터가 저장되지 않을 수 있습니다</strong>.
              <br />
              재로그인 후 게임을 시작하는 것을 권장합니다.
            </p>
            <div className="exit-confirm-buttons">
              <button
                onClick={() => {
                  setShowSessionWarningModal(false);
                  onLoginClick(); // 로그인 모달 열기
                }}
                className="confirm-btn yes-btn"
              >
                재로그인하기
              </button>
              <button
                onClick={() => {
                  setShowSessionWarningModal(false);
                  startGame(); // 경고 무시하고 시작
                }}
                className="confirm-btn no-btn"
              >
                무시하고 시작
              </button>
            </div>
          </div>
        </div>
      )}

      <main
        className={`main-content ${showRankingModal ? 'ranking-view' : ''}`}
      >
        {!showRankingModal && (
          <>
            <div className="chart-card">
              <div className="chart-header-row">
                <div className="chart-header-left">
                  <span className="chart-title">실시간 캔들차트</span>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '1rem',
                      flexWrap: 'wrap',
                    }}
                  >
                    <span className="chart-subinfo">
                      거래량:{' '}
                      {gameState.history.length > 0
                        ? gameState.history[
                            gameState.history.length - 1
                          ].volume.toLocaleString()
                        : 0}{' '}
                      | 변동:{' '}
                      {gameState.history.length > 0
                        ? gameState.history[
                            gameState.history.length - 1
                          ].changePercent.toFixed(2)
                        : 0}
                      % | 누적변동:{' '}
                      {Number.isFinite(changeRate)
                        ? changeRate.toFixed(2)
                        : '0.00'}
                      %
                    </span>
                    {gameState.isGameActive && (
                      <div className="chart-controls" style={{ margin: 0 }}>
                        <label className="ohlc-toggle">
                          <input
                            type="checkbox"
                            checked={showOHLCInfo}
                            onChange={e => setShowOHLCInfo(e.target.checked)}
                          />
                          <span>OHLC 정보 표시</span>
                        </label>
                      </div>
                    )}
                  </div>
                </div>
                <div className="chart-header-right">
                  {gameState.isGameActive && (
                    <>
                      <div className="candle-counter">
                        봉: {gameState.candleCount}/50
                      </div>
                      <span
                        className="info-tooltip"
                        aria-label="게임 종료/강제 청산 안내"
                      >
                        i
                        <span className="info-tooltip-text">
                          게임 종료 버튼을 누르거나 창을 닫거나, 50봉이 지나면 보유
                          포지션이 마지막 봉 종가 기준으로 강제 청산되며 수익률이
                          책정됩니다.
                        </span>
                      </span>
                      <button
                        onClick={() => setShowExitConfirmPopup(true)}
                        className="header-close-btn"
                        title="게임 종료"
                      >
                        🔒 종료
                      </button>
                    </>
                  )}
                </div>
              </div>
              <div className="chart-container">
                {!gameState.isGameActive ? (
                  <div className="game-start-container">
                    <h2>차트 트레이너</h2>
                    <p>게임을 시작하면 실시간 주식차트가 표시됩니다.</p>

                    {!user ? (
                      <div className="login-required">
                        <div className="login-required-icon">🔒</div>
                        <h3>로그인이 필요합니다</h3>
                        <p>게임을 시작하려면 먼저 로그인해주세요.</p>
                        <button
                          onClick={onLoginClick}
                          className="login-required-btn"
                        >
                          로그인하기
                        </button>
                      </div>
                    ) : (
                      <>
                        <div className="mode-selection">
                          <label>
                            <input
                              type="radio"
                              name="mode"
                              checked={isAutoMode}
                              onChange={() => setIsAutoMode(true)}
                            />
                            자동 생성
                          </label>
                          <label>
                            <input
                              type="radio"
                              name="mode"
                              checked={!isAutoMode}
                              onChange={() => setIsAutoMode(false)}
                            />
                            수동 생성
                          </label>
                        </div>
                        <button
                          onClick={() => {
                            console.log('🖱️ 게임 시작 버튼 클릭됨!');
                            
                            // 세션 체크
                            const refreshToken = loadRefreshToken();
                            if (refreshToken) {
                              const decoded = decodeJwt(refreshToken);
                              if (decoded?.exp) {
                                const expiresIn = (decoded.exp * 1000) - Date.now();
                                const remainingSeconds = Math.floor(expiresIn / 1000);
                                
                                // 남은 시간이 30분(1800초) 미만이면 경고
                                if (remainingSeconds < 1800) {
                                  setSessionRemainingSeconds(remainingSeconds);
                                  setShowSessionWarningModal(true);
                                  return; // 게임 시작 중단
                                }
                              }
                            }
                            
                            startGame();
                          }}
                          className="start-game-btn"
                        >
                          🚀 게임 시작
                        </button>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="chart-wrapper">
                    <div className="candlestick-wrapper">
                      <StockChart
                        data={gameState.history.map(d => ({
                          time: d.timestamp,
                          open: d.open,
                          high: d.high,
                          low: d.low,
                          close: d.close,
                          volume: d.volume,
                        }))}
                        volumeData={gameState.history.map(d => ({
                          time: d.timestamp,
                          value: d.volume,
                        }))}
                        ma5={ma5}
                        ma10={ma10}
                        ma20={ma20}
                        ma60={ma60}
                        ma120={ma120}
                        theme={theme}
                        showOHLCInfo={showOHLCInfo}
                        onToggleOHLCInfo={setShowOHLCInfo}
                      />
                    </div>

                    {!isAutoMode && (
                      <div className="manual-controls">
                        <button
                          onClick={generateNextCandle}
                          className="next-candle-btn"
                        >
                          다음 캔들 생성
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="trade-card">
              <div className="trade-panel">
                <div className="balance-info">
                  <div className="balance-item">
                    <span className="balance-label">보유 현금</span>
                    <span className="balance-value">
                      {formatBalance(gameState.playerBalance)}
                    </span>
                  </div>
                  <div className="balance-item">
                    <span className="balance-label">보유 주식</span>
                    <span className="balance-value">
                      {gameState.stockQuantity.toLocaleString()}주
                    </span>
                  </div>
                  <div className="balance-item">
                    <span className="balance-label">평균 단가</span>
                    <span className="balance-value">
                      {gameState.averagePrice > 0
                        ? formatPrice(gameState.averagePrice)
                        : '-'}
                    </span>
                  </div>
                  <div className="balance-item">
                    <span className="balance-label">
                      주식 평가액
                      <button
                        className="balance-info-btn"
                        onClick={() => setShowFeeInfoModal(true)}
                        title="매도 수수료 및 거래세 제외된 금액"
                      >
                        ℹ️
                      </button>
                    </span>
                    <span className="balance-value">
                      {formatBalance(
                        calculateStockValue(
                          gameState.currentPrice,
                          gameState.stockQuantity
                        )
                      )}
                      {gameState.stockQuantity > 0 &&
                        gameState.averagePrice > 0 && (
                          <span
                            className={`profit-rate ${
                              gameState.currentPrice > gameState.averagePrice
                                ? 'positive'
                                : gameState.currentPrice <
                                  gameState.averagePrice
                                ? 'negative'
                                : 'neutral'
                            }`}
                          >
                            (
                            {gameState.currentPrice > gameState.averagePrice
                              ? '+'
                              : ''}
                            {(
                              ((gameState.currentPrice -
                                gameState.averagePrice) /
                                gameState.averagePrice) *
                              100
                            ).toFixed(2)}
                            %)
                          </span>
                        )}
                    </span>
                  </div>
                  <div className="balance-item">
                    <span className="balance-label">
                      총 자산
                      <button
                        className="balance-info-btn"
                        onClick={() => setShowFeeInfoModal(true)}
                        title="주식 평가액에 매도 수수료 및 거래세 제외"
                      >
                        ℹ️
                      </button>
                    </span>
                    <span className="balance-value">
                      {(() => {
                        // 대기 중인 지정가 매수 주문 예약금 계산
                        const pendingBuyOrdersReservedAmount =
                          gameState.limitOrders
                            .filter(
                              order =>
                                order.type === 'buy' &&
                                order.status === 'pending'
                            )
                            .reduce(
                              (sum, order) => sum + order.price * order.size,
                              0
                            );

                        // 주식 평가액은 매도 수수료와 거래세를 차감한 금액
                        const stockValue = calculateStockValue(
                          gameState.currentPrice,
                          gameState.stockQuantity
                        );
                        const totalAssets =
                          gameState.playerBalance +
                          stockValue +
                          pendingBuyOrdersReservedAmount;

                        return formatBalance(totalAssets);
                      })()}
                      {(() => {
                        // 대기 중인 지정가 매수 주문 예약금 계산
                        const pendingBuyOrdersReservedAmount =
                          gameState.limitOrders
                            .filter(
                              order =>
                                order.type === 'buy' &&
                                order.status === 'pending'
                            )
                            .reduce(
                              (sum, order) => sum + order.price * order.size,
                              0
                            );

                        // 주식 평가액은 매도 수수료와 거래세를 차감한 금액
                        const stockValue = calculateStockValue(
                          gameState.currentPrice,
                          gameState.stockQuantity
                        );
                        const totalAssets =
                          gameState.playerBalance +
                          stockValue +
                          pendingBuyOrdersReservedAmount;
                        // 총자산이 0원이면 -100% 반환
                        if (totalAssets <= 0) {
                          return (
                            <span className="profit-rate negative">
                              (-100.00%)
                            </span>
                          );
                        }
                        const totalProfitRate =
                          ((totalAssets - gameState.initialBalance) /
                            gameState.initialBalance) *
                          100;
                        return (
                          <span
                            className={`profit-rate ${
                              totalProfitRate > 0
                                ? 'positive'
                                : totalProfitRate < 0
                                ? 'negative'
                                : 'neutral'
                            }`}
                          >
                            ({totalProfitRate > 0 ? '+' : ''}
                            {totalProfitRate.toFixed(2)}%)
                          </span>
                        );
                      })()}
                    </span>
                  </div>
                </div>

                <div className="trade-section">
                  <div className="trade-section-header">
                    <h4 className="section-title">거래</h4>
                  </div>

                  <div className="order-type-selector">
                    <label className="order-type-label">
                      <input
                        type="checkbox"
                        checked={isMarketOrder}
                        onChange={e => setIsMarketOrder(e.target.checked)}
                      />
                      <span className="order-type-text">
                        {isMarketOrder ? '📈 시장가' : '📋 지정가'}
                      </span>
                    </label>
                  </div>

                  <div className="trade-tabs">
                    <button
                      className={`trade-tab ${
                        activeTradeTab === 'buy' ? 'active' : ''
                      }`}
                      onClick={() => setActiveTradeTab('buy')}
                      data-tab="buy"
                    >
                      🚀 매수
                    </button>
                    <button
                      className={`trade-tab ${
                        activeTradeTab === 'sell' ? 'active' : ''
                      }`}
                      onClick={() => setActiveTradeTab('sell')}
                      data-tab="sell"
                    >
                      💥 매도
                    </button>
                  </div>

                  {activeTradeTab === 'buy' && (
                    <div className="trade-tab-content">
                      <div className="quantity-input-compact">
                        <label>수량</label>
                        <input
                          type="text"
                          value={quantity}
                          onChange={e => {
                            const value = e.target.value;
                            const numericValue = value.replace(/[^0-9]/g, '');
                            setQuantity(numericValue);
                          }}
                          onKeyPress={e => {
                            if (
                              !/[0-9]/.test(e.key) &&
                              e.key !== 'Backspace' &&
                              e.key !== 'Delete' &&
                              e.key !== 'ArrowLeft' &&
                              e.key !== 'ArrowRight' &&
                              e.key !== 'Tab'
                            ) {
                              e.preventDefault();
                            }
                          }}
                          placeholder="수량 입력"
                        />
                      </div>

                      {!isMarketOrder && (
                        <div className="price-input-compact">
                          <label>
                            가격 (호가단위:{' '}
                            {getTickSize(gameState.currentPrice)}원)
                          </label>
                          <input
                            type="number"
                            value={limitOrderPrice}
                            onChange={e => {
                              const value = Number(e.target.value);
                              if (value > 0) {
                                const adjustedPrice = adjustToTickSize(value);
                                setLimitOrderPrice(adjustedPrice.toString());
                              } else {
                                setLimitOrderPrice(e.target.value);
                              }
                            }}
                            onBlur={e => {
                              const value = Number(e.target.value);
                              if (value > 0) {
                                const adjustedPrice = adjustToTickSize(value);
                                setLimitOrderPrice(adjustedPrice.toString());
                              }
                            }}
                            placeholder="가격 입력"
                            min="0"
                            step={getTickSize(gameState.currentPrice)}
                          />
                        </div>
                      )}

                      {quantity && Number(quantity) > 0 && (
                        <div className="trading-cost-info">
                          {(() => {
                            const orderPrice = isMarketOrder
                              ? gameState.currentPrice
                              : adjustToTickSize(
                                  Number(limitOrderPrice) ||
                                    gameState.currentPrice
                                );
                            const quantityNum = Number(quantity);
                            const buyCost = calculateBuyCost(
                              orderPrice,
                              quantityNum
                            );
                            // 매도 비용은 현재 시장 가격 기준으로 동적으로 계산
                            const currentSellCost =
                              calculateBrokerageFee(
                                gameState.currentPrice,
                                quantityNum
                              ) +
                              calculateTransactionTax(
                                gameState.currentPrice,
                                quantityNum
                              );
                            return (
                              <div className="cost-breakdown">
                                <div className="cost-item">
                                  <span className="cost-label">매수 비용:</span>
                                  <span className="cost-value">
                                    {formatPrice(buyCost)}
                                  </span>
                                </div>
                                <div className="cost-item">
                                  <span className="cost-label">
                                    예상 매도 비용:
                                  </span>
                                  <span className="cost-value highlight">
                                    {formatPrice(currentSellCost)}
                                  </span>
                                </div>
                                <div className="cost-note">
                                  (현재가 기준: 매도 수수료 + 거래세)
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      )}

                      <button
                        onClick={buy}
                        className="trade-action-btn buy-btn"
                        disabled={
                          !gameState.isGameActive ||
                          gameState.playerBalance <= 0 ||
                          !quantity ||
                          Number(quantity) <= 0 ||
                          (!isMarketOrder &&
                            (!limitOrderPrice || Number(limitOrderPrice) <= 0))
                        }
                      >
                        📈 매수
                      </button>

                      <div className="percentage-buttons">
                        {[10, 20, 30, 50, 100].map(percent => (
                          <button
                            key={percent}
                            onClick={event => {
                              calculateBuyPercentage(percent);
                              const btn = event.target as HTMLButtonElement;
                              if (btn) {
                                btn.style.transform = 'scale(0.95)';
                                setTimeout(() => {
                                  btn.style.transform = '';
                                }, 150);
                              }
                            }}
                            className="percentage-btn buy-percentage"
                            disabled={
                              !gameState.isGameActive ||
                              gameState.playerBalance <= 0
                            }
                          >
                            {percent === 100 ? '전액' : `${percent}%`}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {activeTradeTab === 'sell' && (
                    <div className="trade-tab-content">
                      <div className="quantity-input-compact">
                        <label>수량</label>
                        <input
                          type="text"
                          value={quantity}
                          onChange={e => {
                            const value = e.target.value;
                            const numericValue = value.replace(/[^0-9]/g, '');
                            setQuantity(numericValue);
                          }}
                          onKeyPress={e => {
                            if (
                              !/[0-9]/.test(e.key) &&
                              e.key !== 'Backspace' &&
                              e.key !== 'Delete' &&
                              e.key !== 'ArrowLeft' &&
                              e.key !== 'ArrowRight' &&
                              e.key !== 'Tab'
                            ) {
                              e.preventDefault();
                            }
                          }}
                          placeholder="수량 입력"
                        />
                      </div>

                      {!isMarketOrder && (
                        <div className="price-input-compact">
                          <label>
                            가격 (호가단위:{' '}
                            {getTickSize(gameState.currentPrice)}원)
                          </label>
                          <input
                            type="number"
                            value={limitOrderPrice}
                            onChange={e => {
                              const value = Number(e.target.value);
                              if (value > 0) {
                                const adjustedPrice = adjustToTickSize(value);
                                setLimitOrderPrice(adjustedPrice.toString());
                              } else {
                                setLimitOrderPrice(e.target.value);
                              }
                            }}
                            onBlur={e => {
                              const value = Number(e.target.value);
                              if (value > 0) {
                                const adjustedPrice = adjustToTickSize(value);
                                setLimitOrderPrice(adjustedPrice.toString());
                              }
                            }}
                            placeholder="가격 입력"
                            min="0"
                            step={getTickSize(gameState.currentPrice)}
                          />
                        </div>
                      )}

                      <button
                        onClick={sell}
                        className="trade-action-btn sell-btn"
                        disabled={
                          !gameState.isGameActive ||
                          gameState.stockQuantity <= 0 ||
                          !quantity ||
                          Number(quantity) <= 0 ||
                          (!isMarketOrder &&
                            (!limitOrderPrice || Number(limitOrderPrice) <= 0))
                        }
                      >
                        📉 매도
                      </button>

                      <div className="percentage-buttons">
                        {[10, 20, 30, 50, 100].map(percent => (
                          <button
                            key={percent}
                            onClick={event => {
                              calculateSellPercentage(percent);
                              const btn = event.target as HTMLButtonElement;
                              if (btn) {
                                btn.style.transform = 'scale(0.95)';
                                setTimeout(() => {
                                  btn.style.transform = '';
                                }, 150);
                              }
                            }}
                            className="percentage-btn sell-percentage"
                            disabled={
                              !gameState.isGameActive ||
                              gameState.stockQuantity <= 0
                            }
                          >
                            {percent === 100 ? '전량' : `${percent}%`}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="trade-section">
                  <h4 className="section-title">지정가 주문</h4>
                  <div className="limit-orders-container">
                    {gameState.limitOrders.filter(
                      order => order.status === 'pending'
                    ).length === 0 ? (
                      <div className="limit-orders-empty">
                        대기 중인 주문 없음
                      </div>
                    ) : (
                      <ul className="limit-orders-list">
                        {gameState.limitOrders
                          .filter(order => order.status === 'pending')
                          .map(order => (
                            <li
                              key={order.id}
                              className={`limit-order-item ${order.type}`}
                            >
                              <div className="order-info">
                                <span className="order-type">
                                  {order.type === 'buy' ? '매수' : '매도'}
                                </span>
                                <span className="order-price">
                                  {formatPrice(order.price)}
                                </span>
                              </div>
                              <div className="order-details">
                                <span className="order-size">
                                  {order.size.toLocaleString()}주
                                </span>
                                <span className="order-time">
                                  {order.time.toLocaleTimeString()}
                                </span>
                                <button
                                  onClick={() => cancelLimitOrder(order.id)}
                                  className="cancel-order-btn"
                                >
                                  취소
                                </button>
                              </div>
                            </li>
                          ))}
                      </ul>
                    )}
                  </div>
                </div>

                <div className="trade-section">
                  <h4 className="section-title">거래 내역</h4>
                  <div className="trade-history-container">
                    {tradeHistory.length === 0 ? (
                      <div className="trade-history-empty">거래내역 없음</div>
                    ) : (
                      <ul className="trade-history-list">
                        {renderTradeHistory(tradeHistory)}
                      </ul>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </main>

      {/* 거래세 및 수수료 정보 모달 */}
      {showFeeInfoModal && (
        <div
          className="modal-overlay"
          onClick={() => setShowFeeInfoModal(false)}
        >
          <div
            className="modal-content fee-info-modal"
            onClick={e => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3>거래세 및 수수료 정보</h3>
              <button
                className="modal-close-btn"
                onClick={() => setShowFeeInfoModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="fee-info-section">
                <h4>📊 적용 기준</h4>
                <p className="fee-info-text">
                  <strong>2026년 한국 주식 시장 기준</strong>
                </p>
              </div>

              <div className="fee-info-section">
                <h4>💰 증권사 수수료</h4>
                <div className="fee-detail">
                  <span className="fee-label">수수료율:</span>
                  <span className="fee-value">
                    {(BROKERAGE_FEE_RATE * 100).toFixed(3)}%
                  </span>
                </div>
                <div className="fee-detail">
                  <span className="fee-label">적용 시점:</span>
                  <span className="fee-value">매수/매도 모두</span>
                </div>
                <p className="fee-description">
                  일반적인 온라인 주문 기준 표준 수수료율입니다. (주요 증권사
                  평균: 0.015% ~ 0.05%)
                </p>
              </div>

              <div className="fee-info-section">
                <h4>📈 증권거래세</h4>
                <div className="fee-detail">
                  <span className="fee-label">거래세율:</span>
                  <span className="fee-value">
                    {(TRANSACTION_TAX_RATE * 100).toFixed(2)}%
                  </span>
                </div>
                <div className="fee-detail">
                  <span className="fee-label">적용 시점:</span>
                  <span className="fee-value">매도 시에만</span>
                </div>
                <p className="fee-description">
                  2026년 기준 증권거래세율입니다. (2025년 0.15%에서 0.20%로
                  인상)
                </p>
              </div>

              <div className="fee-info-section">
                <h4>💡 거래 비용 예시</h4>
                <div className="fee-example">
                  <p>
                    <strong>매수 시 (100만원 기준):</strong>
                  </p>
                  <ul>
                    <li>체결 금액: ₩1,000,000</li>
                    <li>
                      수수료: ₩
                      {Math.floor(
                        1000000 * BROKERAGE_FEE_RATE
                      ).toLocaleString()}
                    </li>
                    <li>
                      총 비용: ₩
                      {Math.floor(
                        1000000 * (1 + BROKERAGE_FEE_RATE)
                      ).toLocaleString()}
                    </li>
                  </ul>
                </div>
                <div className="fee-example">
                  <p>
                    <strong>매도 시 (100만원 기준):</strong>
                  </p>
                  <ul>
                    <li>체결 금액: ₩1,000,000</li>
                    <li>
                      수수료: ₩
                      {Math.floor(
                        1000000 * BROKERAGE_FEE_RATE
                      ).toLocaleString()}
                    </li>
                    <li>
                      거래세: ₩
                      {Math.floor(
                        1000000 * TRANSACTION_TAX_RATE
                      ).toLocaleString()}
                    </li>
                    <li>
                      실제 수익: ₩
                      {Math.floor(
                        1000000 *
                          (1 - BROKERAGE_FEE_RATE - TRANSACTION_TAX_RATE)
                      ).toLocaleString()}
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
